import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerClub } from '../../../api/RegistrationAPI';
import { AuthContext } from '../../AuthContext';

const ClubRegistrationPage = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        // User fields
        name: '',
        phone_number: '',
        password: '',
        password_confirmation: '',
        // Club fields - now more comprehensive
        club_name: '',
        club_location: '',
        established_date: '',
        logo: null,
    });
    const [logoPreview, setLogoPreview] = useState(null); // For showing logo preview
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'logo' && files && files[0]) {
            const file = files[0];
            setFormData(prevData => ({ ...prevData, logo: file }));
            setLogoPreview(URL.createObjectURL(file));
        } else {
            setFormData(prevData => ({ ...prevData, [name]: value }));
        }
    };

    const validateStep = (currentStep) => {
        const newErrors = {};
        if (currentStep === 1) {
            if (!formData.club_name) newErrors.club_name = 'Club name is required.';
            if (!formData.established_date) newErrors.established_date = 'Established date is required.';
        } else if (currentStep === 2) {
            if (!formData.name) newErrors.name = 'Your name is required.';
            if (!formData.phone_number) newErrors.phone_number = 'Phone number is required.';
            if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters.';
            if (formData.password !== formData.password_confirmation) newErrors.password_confirmation = 'Passwords do not match.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNextStep = () => {
        if (validateStep(step)) {
            setStep(prev => prev + 1);
        }
    };

    const handlePrevStep = () => setStep(prev => prev - 1);

    const handleSubmit = async (event) => {
        event.preventDefault();
        // Validate the final step before submitting
        if (!validateStep(2)) return;

        setIsLoading(true);
        setErrors({});

        registerClub(formData, (response) => {
            setIsLoading(false);
            if (response.token) {
                login(response.token);
                // Navigate to the new plan selection page
                navigate('/club_portal/select-plan', { state: { message: 'Registration successful! Please choose a plan to continue.' } });
            } else {
                setErrors(response?.response?.data?.errors || { form: 'An unexpected error occurred.' });
            }
        });
    };

    const progressPercentage = step === 1 ? 50 : 100;

    return (
        <div className='sign-in-bg'>
            <div className="app-wrapper d-block">
                <div className="container main-container">
                    <div className="row main-content-box">
                        <div className="col-lg-7 image-content-box d-none d-lg-block">
                            <div className="form-container">
                                <div className="signup-content mt-4"><span>
                                    <img alt="Logo" className="img-fluid" style={{ maxWidth: "400px" }} src={process.env.REACT_APP_API_BASE_URL + '/logo_files/cricketmanagernewlogowithouttext.png'} />
                                    </span></div>
                                <div className="signup-bg-img"><img alt="Illustration" className="img-fluid" src={process.env.REACT_APP_API_BASE_URL + '/posters/3.png'} /></div>
                            </div>
                        </div>
                        <div className="col-lg-5 form-content-box">
                            <div className="form-container">
                                <form onSubmit={handleSubmit} className="app-form">
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="mb-4 text-center text-lg-start">
                                                <h2 className="text-white f-w-600">Welcome to Cricket Manager</h2>
                                                <p className="f-s-16 mt-2">Register your club.</p>
                                            </div>
                                            {/* Visual Progress Bar */}
                                            <div className="progress mb-4" style={{ height: '10px' }}>
                                                <div className="progress-bar bg-primary" role="progressbar" style={{ width: `${progressPercentage}%` }} aria-valuenow={progressPercentage} aria-valuemin="0" aria-valuemax="100"></div>
                                            </div>
                                        </div>

                                        {errors.form && <div className="col-12 alert alert-danger">{errors.form}</div>}

                                        {step === 1 && (
                                            <>
                                                <h4 className="text-white mb-3"><i className="fas fa-shield-alt me-2"></i>Step 1: Your Club's Profile</h4>
                                                <div className="col-12 form-floating mb-3">
                                                    <input className="form-control" name="club_name" value={formData.club_name} onChange={handleInputChange} placeholder="Club Name" type="text" />
                                                    <label>Club Name</label>
                                                    {errors.club_name && <small className="text-danger ps-2">{errors.club_name}</small>}
                                                </div>
                                                <div className="col-12 form-floating mb-3">
                                                    <input className="form-control" name="club_location" value={formData.club_location} onChange={handleInputChange} placeholder="Club Location (e.g., City)" type="text" />
                                                    <label>Club Location (e.g., City)</label>
                                                </div>
                                                <div className="col-12 form-floating mb-3">
                                                    <input className="form-control" name="established_date" value={formData.established_date} onChange={handleInputChange} placeholder="Established Date" type="date" />
                                                    <label>Established Date</label>
                                                    {errors.established_date && <small className="text-danger ps-2">{errors.established_date}</small>}
                                                </div>
                                                <div className="col-12 mb-3">
                                                    <label className="form-label text-white">Club Logo (Optional)</label>
                                                    <input className="form-control" name="logo" onChange={handleInputChange} type="file" accept="image/png, image/jpeg" />
                                                </div>
                                                {logoPreview && (
                                                    <div className="col-12 text-center mb-3">
                                                        <img src={logoPreview} alt="Club Logo Preview" style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover' }} />
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {step === 2 && (
                                             <>
                                                <h4 className="text-white mb-3"><i className="fas fa-user-circle me-2"></i>Step 2: Your Account</h4>
                                                <div className="col-12 form-floating mb-3">
                                                    <input className="form-control" name="name" value={formData.name} onChange={handleInputChange} placeholder="Your Full Name" type="text" />
                                                    <label>Your Full Name</label>
                                                    {errors.name && <small className="text-danger ps-2">{errors.name}</small>}
                                                </div>
                                                <div className="col-12 form-floating mb-3">
                                                    <input className="form-control" name="phone_number" value={formData.phone_number} onChange={handleInputChange} placeholder="Phone Number" type="tel" />
                                                    <label>Phone Number</label>
                                                    {errors.phone_number && <small className="text-danger ps-2">{errors.phone_number}</small>}
                                                </div>
                                                <div className="col-12 form-floating mb-3">
                                                    <input className="form-control" name="password" value={formData.password} onChange={handleInputChange} placeholder="Create Password" type="password" />
                                                    <label>Create Password</label>
                                                    {errors.password && <small className="text-danger ps-2">{errors.password}</small>}
                                                </div>
                                                <div className="col-12 form-floating mb-3">
                                                    <input className="form-control" name="password_confirmation" value={formData.password_confirmation} onChange={handleInputChange} placeholder="Confirm Password" type="password" />
                                                    <label>Confirm Password</label>
                                                    {errors.password_confirmation && <small className="text-danger ps-2">{errors.password_confirmation}</small>}
                                                </div>
                                            </>
                                        )}

                                        <div className="col-12 mt-3 d-flex justify-content-between">
                                            {step > 1 && <button type="button" className="btn btn-outline-light" onClick={handlePrevStep} disabled={isLoading}>Previous</button>}
                                            {step < 2 && <button type="button" className="btn btn-primary ms-auto" onClick={handleNextStep}>Next</button>}
                                            {step === 2 && <button type="submit" className="btn btn-primary btn-lg ms-auto" disabled={isLoading}>{isLoading ? 'Creating Account...' : 'Create Account & Proceed'}</button>}
                                        </div>

                                        <div className="col-12 mt-4">
                                            <div className="text-center text-lg-start f-w-500">
                                                Already have an account?
                                                <Link to="/login" className="text-white-800 text-decoration-underline ms-1">Sign In</Link>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClubRegistrationPage;

